import { get, post, patch, del } from '../httpMethods';
import { ApiResponse, PaginationMeta } from '../../types/api';

export interface Ward {
  id: string;
  wardCode: string;
  wardName: string;
  wardType: 'GENERAL' | 'SEMI_PRIVATE' | 'PRIVATE' | 'ICU' | 'NICU' | 'CCU' | 'HDU';
  rooms?: {
    id: string;
    name: string;
    floor?: { id: string; name: string; floorNumber?: number } | null;
  }[];
  floor?: string;
  totalBeds: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateWardDto {
  wardCode: string;
  wardName: string;
  wardType: Ward['wardType'];
  roomIds?: string[];
  floor?: string;
  totalBeds: number;
  isActive?: boolean;
}

export interface WardListFilters {
  search?: string;
  wardType?: string;
  isActive?: boolean;
}

export const wardService = {
  getWards: (
    page = 1,
    limit = 20,
    filters?: WardListFilters,
  ): Promise<ApiResponse<Ward[]> & { meta: PaginationMeta }> => {
    const params = new URLSearchParams({ page: String(page), limit: String(limit) });
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.set(key, String(value));
        }
      });
    }
    return get<Ward[]>(`/wards?${params.toString()}`) as Promise<
      ApiResponse<Ward[]> & { meta: PaginationMeta }
    >;
  },

  getWard: (id: string): Promise<ApiResponse<Ward>> => {
    return get<Ward>(`/wards/${id}`);
  },

  getWardBeds: (id: string): Promise<ApiResponse<any[]>> => {
    return get<any[]>(`/wards/${id}/beds`);
  },

  createWard: (data: CreateWardDto): Promise<ApiResponse<Ward>> => {
    return post<Ward>('/wards', data);
  },

  updateWard: (id: string, data: Partial<CreateWardDto>): Promise<ApiResponse<Ward>> => {
    return patch<Ward>(`/wards/${id}`, data);
  },

  deleteWard: (id: string): Promise<ApiResponse<void>> => {
    return del<void>(`/wards/${id}`);
  },

  bulkDeleteWards: (
    ids: string[],
  ): Promise<ApiResponse<{ deleted: string[]; notFound: string[] }>> => {
    return post<{ deleted: string[]; notFound: string[] }>('/wards/bulk-delete', { ids });
  },

  getOccupancySummary: (): Promise<ApiResponse<any[]>> => {
    return get<any[]>('/wards/occupancy/summary');
  },
};
